import React, { useState, useEffect, useCallback } from 'react'
import { Pagination, Row, Col, Collapse } from 'antd'
import moment from 'moment'
import { CaretRightOutlined } from '@ant-design/icons'
import axios from 'axios'

import PageHeader from '../Layout/PageHeader'
import OrdersList from './OrdersList'
import OrderButtons from './OrderButtons'
import CodStats from './CodStats'
import CodOrderFilters from './CodOrderFilters'

import { usePermissions } from "../App/PermissionContext";

const today = moment()
const oneWeekAgo = moment().subtract(7, 'days')

const CodOrder = () => {
  const { permissions } = usePermissions();
  const [orders, setOrders] = useState([])

  const [filters, setFilters] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const internalStatusParam = urlParams.get('internalstatus');
    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');
    const codCurrencyCode = urlParams.get('codCurrencyCode');
    const isRemittance = urlParams.get('isRemittance') === 'true'
    const isNextDayRemittance = urlParams.get('isNextDayRemittance') === 'true'
    const sortBy = urlParams.get('sortBy')

    const baseFilters = {
      isRemittance: isRemittance || null,
      isNextDayRemittance: isNextDayRemittance || null,
      completeDelivered: null,
      sortBy: sortBy || "paymentDate",
      codCurrencyCode: codCurrencyCode || "RM",
      internalStatus: internalStatusParam ? internalStatusParam.split(',') : undefined,
    };

    if (sortBy === "remittedDate") {
      return {
        ...baseFilters,
        remittedStartDate: startDateParam ? moment(startDateParam) : oneWeekAgo,
        remittedEndDate: endDateParam ? moment(endDateParam) : today,
        paymentStartDate: null,
        paymentEndDate: null,
        startDate: null,
        endDate: null,
      };
    } else {
      return {
        ...baseFilters,
        paymentStartDate: startDateParam ? moment(startDateParam) : oneWeekAgo,
        paymentEndDate: endDateParam ? moment(endDateParam) : today,
        remittedStartDate: null,
        remittedEndDate: null,
        startDate: null,
        endDate: null,
      };
    }
  });
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 })
  const [meta, setMeta] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])

  const fetchOrders = useCallback(async () => {
    setIsLoading(true)

    const params = {
      activeOnly: true,
      codOnly: true,
      codCurrencyCode: filters.codCurrencyCode,
      ...(filters.startDate !== null) && { startDate: filters.startDate?.startOf('day')?.toISOString() },
      ...(filters.endDate !== null) && { endDate: filters.endDate?.endOf('day')?.toISOString() },
      ...(filters.remittedStartDate !== null) && { remittedStartDate: filters.remittedStartDate?.startOf('day')?.toISOString() },
      ...(filters.remittedEndDate !== null) && { remittedEndDate: filters.remittedEndDate?.endOf('day')?.toISOString() },
      ...(filters.paymentStartDate !== null) && { paymentStartDate: filters.paymentStartDate?.startOf('day')?.toISOString() },
      ...(filters.paymentEndDate !== null) && { paymentEndDate: filters.paymentEndDate?.endOf('day')?.toISOString() },
      ...(filters.isRemittance !== null) && { isRemittance: filters.isRemittance },
      ...(filters.isNextDayRemittance !== null) && { isNextDayRemittance: filters.isNextDayRemittance },
      ...(filters.completeDelivered !== null) && { completeDelivered: filters.completeDelivered },
      ...(filters.internalStatus !== null) && { internalStatus: filters.internalStatus },
      ...(filters.notInternalStatus !== null) && { notInternalStatus: filters.notInternalStatus },
      ...(filters.search && { searchField: filters.searchField, search: filters.search }),
      ...(filters.serviceProvider && { serviceProvider: filters.serviceProvider }),
      limit: pagination.limit,
      page: pagination.currentPage
    }

    const res = await axios.get("/api/orders", { params })

    setOrders(res.data.orders)
    setPagination(res.data.pagination)
    setMeta(res.data.meta)
    setIsLoading(false)
  }, [pagination.currentPage, pagination.limit, filters])

  useEffect(() => {
    fetchOrders()
  }, [pagination.currentPage, pagination.limit, fetchOrders])

  return (
    <div>
      <PageHeader
        title="COD Order"
        childRoutes={[{ path: "/cod-order", breadcrumbName: "COD Order" }]}
      />

      <CodStats
        stats={meta}
        currencyCode={filters.codCurrencyCode}
        onSelect={selected => setFilters({ ...filters, ...selected })}
      />

      <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
        <Collapse.Panel header="Filters" key="filters">
          <CodOrderFilters
            filters={filters}
            onFiltersChange={filters => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row
        className="mt-2 mb-2"
        justify="space-between"
        align="middle"
      >
        <OrderButtons
          selectedRowKeys={selectedRows}
          orders={orders}
          filters={{
            activeOnly: true,
            codOnly: true,
            codCurrencyCode: filters.codCurrencyCode,
            ...(filters.startDate !== null && {
              startDate: filters.startDate?.startOf("day")?.toISOString(),
            }),
            ...(filters.endDate !== null && {
              endDate: filters.endDate?.endOf("day")?.toISOString(),
            }),
            ...(filters.remittedStartDate !== null && {
              startDate: filters.startDate?.startOf("day")?.toISOString(),
            }),
            ...(filters.remittedEndDate !== null && {
              endDate: filters.endDate?.endOf("day")?.toISOString(),
            }),
            ...(filters.paymentStartDate !== null && {
              paymentStartDate: filters.paymentStartDate
                ?.startOf("day")
                ?.toISOString(),
            }),
            ...(filters.paymentEndDate !== null && {
              paymentEndDate: filters.paymentEndDate
                ?.endOf("day")
                ?.toISOString(),
            }),
            ...(filters.isRemittance !== null && {
              isRemittance: filters.isRemittance,
            }),
            ...(filters.isNextDayRemittance !== null && {
              isNextDayRemittance: filters.isNextDayRemittance,
            }),
            ...(filters.completeDelivered !== null && {
              completeDelivered: filters.completeDelivered,
            }),
            ...(filters.internalStatus !== null) && { internalStatus: filters.internalStatus },
            ...(filters.notInternalStatus !== null) && { notInternalStatus: filters.notInternalStatus },
            ...(filters.search && {
              searchField: filters.searchField,
              search: filters.search,
            }),
            ...(filters.serviceProvider && {
              serviceProvider: filters.serviceProvider,
            }),
            pagination: pagination,
          }}
          onRefunded={() => fetchOrders()}
        />


        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={((currentPage, limit) => { setPagination({ ...pagination, limit, currentPage }) })}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <OrdersList
        orders={orders}
        onSelectRows={selectedRowKeys => setSelectedRows(selectedRowKeys)}
        selectedRows={selectedRows}
        loading={isLoading}

      />

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={((currentPage, limit) => { setPagination({ ...pagination, limit, currentPage }) })}
            current={pagination.currentPage}
          />
        </Col>
      </Row>
    </div>
  )
}

export default CodOrder